<template>
  <div class="text-center">
    <template v-if="step != '2fa'">
      <h5 class="text-center">{{ texts.loginTitle }}</h5>
      <h6 class="text--disabled text-center font-weight-medium mb-10">
        {{
          !cacheUser
            ? `Informe seu WhatsApp para começar`
            : `É bom te ver de volta ${
                cacheUser.name.split(" ")[0]
              }, informe sua senha para continuar`
        }}
      </h6>

      <div v-show="!cacheUser">
        <phone-input
          :phone="whatsapp"
          @update:phone="(e) => (whatsapp = e)"
          :ddi="ddi"
          @update:ddi="(e) => (ddi = e)"
          @valid="(v) => (valid = v)"
          dense
          outlined
          :disabled="loading"
        />
      </div>
      <div class="d-flex text-left align-center mb-3" v-if="!!cacheUser">
        <base-avatar
          :size="50"
          :src="cacheUser.photo"
          :seed="cacheUser.id"
          class="mr-3"
        />
        <div class="flex-grow-1">
          <h6 class="font-weight-medium mb-0">{{ cacheUser.name }}</h6>
          <p class="text--disabled mb-0">{{ cacheUser.phone }}</p>
        </div>
        <v-btn icon @click="cCacheUser" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-text-field
        v-on:keyup.enter="processRecaptcha"
        v-show="step == 'login'"
        name="input-10-2"
        label="Senha"
        type="password"
        ref="password"
        :disabled="loading"
        v-model="password"
        outlined
        dense
      ></v-text-field>

      <v-fade-transition hide-on-leave>
        <v-alert
          v-show="error"
          border="left"
          class="text-left"
          text
          icon="mdi-alert-circle-outline"
          type="error"
        >
          {{ error }}
        </v-alert>
      </v-fade-transition>

      <v-row class="px-3">
        <v-col cols="" class="pa-0">
          <v-btn
            :disabled="!isValid"
            color="primary"
            block
            :loading="loading"
            @click="processRecaptcha"
          >
            Continuar
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <SignIn2FA
        v-if="!expired"
        :authenticators="authenticators"
        :token="token"
        :user="{
          phone: whatsapp,
          ddi: ddi,
        }"
      />
      <div v-else class="d-flex flex-column align-center text-center gap-6">
        <v-icon color="error" size="50">mdi-clock-alert-outline</v-icon>
        <h6 class="text-center font-weight-medium">
          Sessão expirada, por favor, tente novamente
        </h6>
        <v-btn
          color="primary"
          block
          @click="$router.go()"
          :loading="loading"
        >
          Tentar novamente
        </v-btn>
      </div>
    </template>

    <re-captcha
      ref="recaptcha"
      @success="process"
      @error="recaptchaError"
      @close="recaptchaError"
    />
  </div>
</template>

<script>
import ReCaptcha from "@/components/global/ReCaptcha.vue";
import AUTH from "@/services/auth";
import { mapActions, mapGetters } from "vuex";
import PhoneInput from "./PhoneInput.vue";
import { texts } from "@/themeConfig.js";
import SignIn2FA from "./SignIn2FA.vue";

export default {
  components: { ReCaptcha, PhoneInput, SignIn2FA },
  name: "SignUp",
  metaInfo: {
    title: "Acessar conta",
  },
  data() {
    return {
      valid: false,
      texts,
      ddi: 55,
      whatsapp: "",
      password: "",

      token: "",
      authenticators: [],

      timeout: null,
      expired: false,

      step: "identify",
      loading: false,
      loadingWebAuthn: false,
      pageLoading: true,
      error: false,
      nextStep: null,
    };
  },
  methods: {
    ...mapActions("auth", ["setLogin", "clearCacheUser"]),
    processRecaptcha() {
      this.loading = true;
      this.error = false;
      this.$refs.recaptcha.execute();
    },
    async process(recaptcha) {
      if (this.nextStep) {
        let ns = this.nextStep;
        this.nextStep = null;
        return this[ns](recaptcha);
      }

      try {
        this.loading = true;

        if (this.step === "identify") {
          var response = await AUTH.login({
            ddi: this.ddi,
            phone: this.whatsapp,
            redirect:
              this.$route.query.redirect || this.$route.path !== "/auth"
                ? this.$route.path
                : undefined,
            recaptcha,
          });
          if (response.user) {
            this.step = "login";
            this.$nextTick(() => {
              this.$refs.password.focus();
            });
          }
        } else if (this.step === "login") {
          if (!this.password) throw new Error("Senha é obrigatória");
          var response = await AUTH.login({
            ddi: this.ddi,
            phone: this.whatsapp,
            password: this.password,
            recaptcha,
          });

          this.step = "2fa";
          this.authenticators = response.authenticators;
          this.token = response.token;
          this.timeout = setTimeout(() => {
            this.expired = true;
          }, 1000 * 60 * 5 - 5000);
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    recaptchaError(error) {
      this.loading = false;
      this.error = error;
    },
    cCacheUser() {
      this.clearCacheUser();
      this.whatsapp = "";
      this.step = "identify";
      this.authenticators = [];
      this.$nextTick(() => {
        this.$refs.whatsapp.focus();
      });
    },
  },
  computed: {
    ...mapGetters("auth", ["cacheUser"]),
    isValid() {
      return this.valid || !!this.cacheUser;
    },
  },
  watch: {
    whatsapp() {
      if (this.step !== "identify") {
        this.authenticators = [];
        this.step = "identify";
      }
      this.error = false;
    },
  },
  mounted() {
    if (this.cacheUser) {
      this.ddi = this.cacheUser.ddi;
      this.whatsapp = this.cacheUser.phone;
      this.authenticators = this.cacheUser.authenticators || [];
      this.$nextTick(() => {
        this.step = "login";
      });
    }
  },
  props: {
    redirect: {
      type: Boolean,
      default: true,
    },
  },
};
</script>
